import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { 
	MapPin, 
	Mail, 
	Phone, 
	Users, 
	Calendar, 
	Building, 
	GraduationCap, 
	Award, 
	Globe, 
	ExternalLink,
	Linkedin,
	Star
} from "lucide-react";
import { useTranslation } from "react-i18next";

interface LinkedInProfileData {
	participant: {
		object?: string;
		provider?: string;
		provider_id?: string;
		public_identifier?: string;
		member_urn?: string;
		first_name?: string;
		last_name?: string;
		headline?: string;
		primary_locale?: {
			country?: string;
			language?: string;
		};
		is_open_profile?: boolean;
		is_premium?: boolean;
		is_influencer?: boolean;
		is_creator?: boolean;
		is_relationship?: boolean;
		network_distance?: string;
		is_self?: boolean;
		websites?: string[];
		follower_count?: number;
		connections_count?: number;
		location?: string;
		contact_info?: {
			emails?: string[];
			phones?: string[];
		};
		birthdate?: {
			month?: number;
			day?: number;
		};
		profile_picture_url?: string;
		profile_picture_url_large?: string;
		background_picture_url?: string;
		education?: Array<{
			degree?: string;
			school_id?: string;
			school?: string;
			start?: string;
			end?: string;
		}>;
		work_experience?: Array<{
			company_id?: string;
			company?: string;
			position?: string;
			location?: string;
			description?: string;
			skills?: string[];
			start?: string;
			end?: string;
		}>;
		skills?: Array<{
			name?: string;
			endorsement_count?: number;
			insights?: string[];
			endorsement_id?: number;
			endorsed?: boolean;
		}>;
		languages?: Array<{
			name?: string;
			proficiency?: string;
		}>;
		certifications?: Array<{
			name?: string;
			organization?: string;
			url?: string;
		}>;
		summary?: string;
		volunteering_experience?: any[];
		projects?: any[];
		recommendations?: {
			received?: any[];
			given?: any[];
		};
		shared_connections_count?: number;
		creator_website?: {
			url?: string;
			description?: string;
		};
	};
}

interface LinkedInProfileModalProps {
	isOpen: boolean;
	onClose: () => void;
	profileData: LinkedInProfileData | null;
	loading?: boolean;
	error?: string;
	onUpdateCandidate?: () => void;
	showUpdateButton?: boolean;
	candidateExists?: boolean;
}

export const LinkedInProfileModal = ({ 
	isOpen, 
	onClose, 
	profileData, 
	loading = false, 
	error,
	onUpdateCandidate,
	showUpdateButton = false,
	candidateExists = false
}: LinkedInProfileModalProps) => {
	const { t } = useTranslation();
	
	if (!isOpen) return null;

	const participant = profileData?.participant;
	const fullName = `${participant?.first_name || ''} ${participant?.last_name || ''}`.trim();

	const formatDate = (dateString: string) => {
		if (!dateString) return '';
		try {
			const date = new Date(dateString);
			return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short' });
		} catch {
			return dateString;
		}
	};

	const getInitials = (firstName?: string, lastName?: string) => {
		return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
	};

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent className="max-w-4xl max-h-[90vh] p-0 w-full">
				<DialogHeader className="px-6 py-4 border-b">
					<DialogTitle className="flex items-center gap-2">
						<Linkedin className="h-5 w-5 text-blue-500" />
						LinkedIn Profile
					</DialogTitle>
					{showUpdateButton && !loading && !error && profileData && (
						<div className="flex justify-end pt-2">
							<Button 
								onClick={onUpdateCandidate}
								variant="default"
								className="bg-blue-500 hover:bg-blue-500 text-white"
							>
								{candidateExists ? 'Update Candidate' : 'Create Candidate'}
							</Button>
						</div>
					)}
				</DialogHeader>

				<ScrollArea className="max-h-[calc(90vh-120px)]">
					<div className="px-6 py-4 min-w-full">
						{error && (
							<div className="text-center py-8">
								<div className="text-red-600 dark:text-red-400 mb-2">Error loading profile</div>
								<p className="text-gray-600 dark:text-gray-400 text-sm">{error}</p>
							</div>
						)}

						{!error && (
							<div className="space-y-6 min-w-[800px]">
								{/* Header Section */}
								<Card>
									<CardContent className="p-6">
										<div className="flex flex-col md:flex-row gap-6">
											<div className="flex flex-col items-center md:items-start">
												{loading ? (
													<div className="animate-pulse">
														<div className="h-32 w-32 bg-gray-200 dark:bg-gray-700 rounded-sm mb-4"></div>
														<div className="flex gap-2 justify-center">
															<div className="h-6 w-16 bg-gray-200 dark:bg-gray-700 rounded"></div>
															<div className="h-6 w-20 bg-gray-200 dark:bg-gray-700 rounded"></div>
														</div>
													</div>
												) : (
													<>
														<Avatar className="h-32 w-32 mb-4">
															<AvatarImage 
																src={participant?.profile_picture_url} 
																alt={fullName}
															/>
															<AvatarFallback className="text-2xl">
																{getInitials(participant?.first_name, participant?.last_name)}
															</AvatarFallback>
														</Avatar>
														<div className="flex flex-wrap gap-2 justify-center md:justify-start">
															{participant?.is_premium && (
																<Badge variant="secondary" className="bg-yellow-100 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300">
																	<Star className="h-3 w-3 mr-1" />
																	Premium
																</Badge>
															)}
															{participant?.is_open_profile && (
																<Badge variant="secondary" className="bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-300">
																	Open Profile
																</Badge>
															)}
															{participant?.is_influencer && (
																<Badge variant="secondary" className="bg-purple-100 dark:bg-purple-900/40 text-purple-800 dark:text-purple-300">
																	Influencer
																</Badge>
															)}
														</div>
													</>
												)}
											</div>
											
											<div className="flex-1 space-y-3">
												{loading ? (
													<div className="animate-pulse space-y-3">
														<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[300px]"></div>
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[500px]"></div>
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[450px]"></div>
														<div className="space-y-2">
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[350px]"></div>
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[280px]"></div>
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[420px]"></div>
														</div>
														<div className="space-y-2">
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[380px]"></div>
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[320px]"></div>
														</div>
													</div>
												) : (
													<>
														<div>
															<h2 className="text-2xl font-bold">{fullName}</h2>
															{participant?.headline && (
																<p className="text-gray-600 dark:text-gray-400 mt-1">{participant.headline}</p>
															)}
														</div>

														<div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-400">
															{participant?.location && (
																<div className="flex items-center gap-1">
																	<MapPin className="h-4 w-4" />
																	{participant.location}
																</div>
															)}
															{participant?.connections_count && (
																<div className="flex items-center gap-1">
																	<Users className="h-4 w-4" />
																	{participant.connections_count.toLocaleString()} connections
																</div>
															)}
															{participant?.follower_count && (
																<div className="flex items-center gap-1">
																	<Users className="h-4 w-4" />
																	{participant.follower_count.toLocaleString()} followers
																</div>
															)}
														</div>

														{participant?.contact_info && (
															<div className="space-y-2">
																{participant.contact_info.emails?.map((email, index) => (
																	<div key={index} className="flex items-center gap-2 text-sm">
																		<Mail className="h-4 w-4 text-gray-400" />
																		<a href={`mailto:${email}`} className="text-blue-500 hover:underline">
																			{email}
																		</a>
																	</div>
																))}
																{participant.contact_info.phones?.map((phone, index) => (
																	<div key={index} className="flex items-center gap-2 text-sm">
																		<Phone className="h-4 w-4 text-gray-400" />
																		<a href={`tel:${phone}`} className="text-blue-500 hover:underline">
																			{phone}
																		</a>
																	</div>
																))}
															</div>
														)}

														{participant?.websites && participant.websites.length > 0 && (
															<div className="space-y-2">
																{participant.websites.map((website, index) => (
																	<div key={index} className="flex items-center gap-2 text-sm">
																		<Globe className="h-4 w-4 text-gray-400" />
																		<a 
																			href={website} 
																			target="_blank" 
																			rel="noopener noreferrer"
																			className="text-blue-500 hover:underline flex items-center gap-1"
																		>
																			{website}
																			<ExternalLink className="h-3 w-3" />
																		</a>
																	</div>
																))}
															</div>
														)}
													</>
												)}
											</div>
										</div>
									</CardContent>
								</Card>

								{/* Summary Section */}
								<Card>
									<CardHeader>
										<CardTitle>About</CardTitle>
									</CardHeader>
									<CardContent>
										{loading ? (
											<div className="animate-pulse space-y-2">
												<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[600px]"></div>
												<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[650px]"></div>
												<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[580px]"></div>
												<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[620px]"></div>
												<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full min-w-[590px]"></div>
												<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-4/5 min-w-[500px]"></div>
											</div>
										) : participant?.summary ? (
											<p className="text-gray-700 dark:text-gray-300 whitespace-pre-line">{participant.summary}</p>
										) : (
											<p className="text-gray-500 dark:text-gray-400 italic">No summary available</p>
										)}
									</CardContent>
								</Card>

								{/* Work Experience Section */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											<Building className="h-5 w-5" />
											Work Experience
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{loading ? (
											Array.from({ length: 4 }).map((_, index) => (
												<div key={index} className="animate-pulse border-b border-gray-100 dark:border-gray-700 last:border-0 pb-4 last:pb-0">
													<div className="flex justify-between items-start mb-2">
														<div className="space-y-2 flex-1 mr-4">
															<div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-xs"></div>
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-sm"></div>
															<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-md"></div>
														</div>
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 flex-shrink-0"></div>
													</div>
													<div className="space-y-2 mb-3">
														<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
														<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
														<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
														<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
														<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
													</div>
													<div className="flex gap-2">
														<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20"></div>
														<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-24"></div>
														<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-16"></div>
														<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-18"></div>
													</div>
												</div>
											))
										) : participant?.work_experience && participant.work_experience.length > 0 ? (
											participant.work_experience.map((exp, index) => (
												<div key={index} className="border-b border-gray-100 dark:border-gray-700 last:border-0 pb-4 last:pb-0">
													<div className="flex justify-between items-start mb-2">
														<div>
															<h4 className="font-semibold">{exp.position}</h4>
															<p className="text-gray-600 dark:text-gray-400">{exp.company}</p>
															{exp.location && (
																<p className="text-sm text-gray-500 dark:text-gray-400">{exp.location}</p>
															)}
														</div>
														<div className="text-sm text-gray-500 dark:text-gray-400 text-right">
															{formatDate(exp.start)} - {exp.end ? formatDate(exp.end) : 'Present'}
														</div>
													</div>
													{exp.description && (
														<p className="text-gray-700 dark:text-gray-300 text-sm mb-3 whitespace-pre-line">{exp.description}</p>
													)}
													{exp.skills && exp.skills.length > 0 && (
														<div className="flex flex-wrap gap-1">
															{exp.skills.map((skill, skillIndex) => (
																<Badge key={skillIndex} variant="outline" className="text-xs">
																	{skill}
																</Badge>
															))}
														</div>
													)}
												</div>
											))
										) : (
											<p className="text-gray-500 dark:text-gray-400 italic">No work experience available</p>
										)}
									</CardContent>
								</Card>

								{/* Education Section */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											<GraduationCap className="h-5 w-5" />
											Education
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-4">
										{loading ? (
											Array.from({ length: 3 }).map((_, index) => (
												<div key={index} className="animate-pulse border-b border-gray-100 dark:border-gray-700 last:border-0 pb-3 last:pb-0">
													<div className="flex justify-between items-start">
														<div className="space-y-2 flex-1 mr-4">
															<div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-sm"></div>
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-lg"></div>
														</div>
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-32 flex-shrink-0"></div>
													</div>
												</div>
											))
										) : participant?.education && participant.education.length > 0 ? (
											participant.education.map((edu, index) => (
												<div key={index} className="border-b border-gray-100 dark:border-gray-700 last:border-0 pb-3 last:pb-0">
													<div className="flex justify-between items-start">
														<div>
															<h4 className="font-semibold">{edu.school}</h4>
															{edu.degree && (
																<p className="text-gray-600 dark:text-gray-400">{edu.degree}</p>
															)}
														</div>
														<div className="text-sm text-gray-500 dark:text-gray-400">
															{formatDate(edu.start)} - {formatDate(edu.end)}
														</div>
													</div>
												</div>
											))
										) : (
											<p className="text-gray-500 dark:text-gray-400 italic">No education information available</p>
										)}
									</CardContent>
								</Card>

								{/* Skills Section */}
								<Card>
									<CardHeader>
										<CardTitle>Skills</CardTitle>
									</CardHeader>
									<CardContent>
										{loading ? (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
												{Array.from({ length: 12 }).map((_, index) => (
													<div key={index} className="animate-pulse flex items-center justify-between p-3 border rounded-sm">
														<div className="space-y-2 flex-1 mr-4">
															<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full"></div>
															<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
														</div>
														<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20 flex-shrink-0"></div>
													</div>
												))}
											</div>
										) : participant?.skills && participant.skills.length > 0 ? (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
												{participant.skills.map((skill, index) => (
													<div key={index} className="flex items-center justify-between p-3 border rounded-sm">
														<div>
															<h5 className="font-medium">{skill.name}</h5>
															{skill.insights && skill.insights.length > 0 && (
																<p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
																	{skill.insights[0]}
																</p>
															)}
														</div>
														{skill.endorsement_count !== undefined && skill.endorsement_count > 0 && (
															<Badge variant="secondary" className="text-xs">
																{skill.endorsement_count} endorsements
															</Badge>
														)}
													</div>
												))}
											</div>
										) : (
											<p className="text-gray-500 dark:text-gray-400 italic">No skills information available</p>
										)}
									</CardContent>
								</Card>

								{/* Certifications Section */}
								<Card>
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											<Award className="h-5 w-5" />
											Certifications
										</CardTitle>
									</CardHeader>
									<CardContent className="space-y-3">
										{loading ? (
											Array.from({ length: 3 }).map((_, index) => (
												<div key={index} className="animate-pulse flex items-center justify-between p-3 border rounded-sm">
													<div className="space-y-2 flex-1 mr-4">
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-md"></div>
														<div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-sm"></div>
													</div>
													<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-8 flex-shrink-0"></div>
												</div>
											))
										) : participant?.certifications && participant.certifications.length > 0 ? (
											participant.certifications.map((cert, index) => (
												<div key={index} className="flex items-center justify-between p-3 border rounded-sm">
													<div>
														<h5 className="font-medium">{cert.name}</h5>
														<p className="text-gray-600 dark:text-gray-400 text-sm">{cert.organization}</p>
													</div>
													{cert.url && (
														<Button variant="outline" size="sm" asChild>
															<a href={cert.url} target="_blank" rel="noopener noreferrer">
																<ExternalLink className="h-4 w-4" />
															</a>
														</Button>
													)}
												</div>
											))
										) : (
											<p className="text-gray-500 dark:text-gray-400 italic">No certifications available</p>
										)}
									</CardContent>
								</Card>

								{/* Languages Section */}
								<Card>
									<CardHeader>
										<CardTitle>Languages</CardTitle>
									</CardHeader>
									<CardContent>
										{loading ? (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
												{Array.from({ length: 4 }).map((_, index) => (
													<div key={index} className="animate-pulse flex items-center justify-between p-3 border rounded-sm">
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-xs"></div>
														<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full max-w-sm"></div>
													</div>
												))}
											</div>
										) : participant?.languages && participant.languages.length > 0 ? (
											<div className="grid grid-cols-1 md:grid-cols-2 gap-3">
												{participant.languages.map((lang, index) => (
													<div key={index} className="flex items-center justify-between p-3 border rounded-sm">
														<span className="font-medium">{lang.name}</span>
														<span className="text-sm text-gray-600 dark:text-gray-400">{lang.proficiency}</span>
													</div>
												))}
											</div>
										) : (
											<p className="text-gray-500 dark:text-gray-400 italic">No language information available</p>
										)}
									</CardContent>
								</Card>
							</div>
						)}
					</div>
				</ScrollArea>
			</DialogContent>
		</Dialog>
	);
};